import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import Navbar from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { toast } from "sonner";
import {
  ChevronLeft,
  Save,
  Plus,
  Trash2,
  Edit,
  Video,
  Terminal,
  BookOpen,
  GripVertical,
  Eye,
  Loader2,
  CheckCircle,
  X,
  Upload,
  Link as LinkIcon,
  Play,
  Clock,
  Award,
  Sparkles,
  Wand2,
} from "lucide-react";

const CourseEditor = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const isNew = courseId === "new";

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("details");

  // Course data
  const [course, setCourse] = useState({
    title: "",
    description: "",
    short_description: "",
    thumbnail: "",
    price: 49.99,
    category: "devops",
    level: "beginner",
    tags: [],
    is_published: false,
  });

  // Lessons
  const [lessons, setLessons] = useState([]);
  const [loadingLessons, setLoadingLessons] = useState(false);

  // Dialogs
  const [showLessonDialog, setShowLessonDialog] = useState(false);
  const [showLabDialog, setShowLabDialog] = useState(false);
  const [showQuizDialog, setShowQuizDialog] = useState(false);
  const [editingLesson, setEditingLesson] = useState(null);
  const [editingLab, setEditingLab] = useState(null);
  const [editingQuiz, setEditingQuiz] = useState(null);
  const [selectedLessonId, setSelectedLessonId] = useState(null);

  // New tag input
  const [newTag, setNewTag] = useState("");

  // Cover image upload
  const [uploadingImage, setUploadingImage] = useState(false);

  // AI quiz generation
  const [quizAiLoading, setQuizAiLoading] = useState(false);
  const [quizNumQuestions, setQuizNumQuestions] = useState(5);

  // Agent IA - génération de plan
  const [showAiDialog, setShowAiDialog] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [applyingOutline, setApplyingOutline] = useState(false);
  const [aiOutline, setAiOutline] = useState(null);
  const [aiParams, setAiParams] = useState({
    num_modules: 5,
    lessons_per_module: 4,
    reorganize: false,
    replace: false,
  });

  // Agent IA - script vidéo par leçon
  const [showScriptDialog, setShowScriptDialog] = useState(false);
  const [scriptLesson, setScriptLesson] = useState(null);
  const [scriptDetails, setScriptDetails] = useState("");
  const [scriptHtml, setScriptHtml] = useState("");
  const [scriptLoading, setScriptLoading] = useState(false);
  const [savingScript, setSavingScript] = useState(false);

  useEffect(() => {
    if (!isNew) {
      fetchCourse();
      fetchLessons();
    }
  }, [courseId]);

  const fetchCourse = async () => {
    try {
      const response = await axios.get(`${API}/courses/${courseId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCourse(response.data);
    } catch (error) {
      toast.error("Cours non trouvé");
      navigate("/admin");
    } finally {
      setLoading(false);
    }
  };

  const fetchLessons = async () => {
    setLoadingLessons(true);
    try {
      const response = await axios.get(`${API}/admin/courses/${courseId}/lessons`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      // Fetch labs and quizzes for each lesson
      const lessonsWithContent = await Promise.all(
        response.data.map(async (lesson) => {
          try {
            const lessonDetail = await axios.get(`${API}/lessons/${lesson.lesson_id}`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            return {
              ...lesson,
              lab: lessonDetail.data.lab,
              quiz: lessonDetail.data.quiz,
            };
          } catch {
            return lesson;
          }
        })
      );
      
      setLessons(lessonsWithContent);
    } catch (error) {
      console.error("Error fetching lessons:", error);
    } finally {
      setLoadingLessons(false);
    }
  };

  const saveCourse = async () => {
    if (!course.title || !course.description) {
      toast.error("Titre et description requis");
      return;
    }

    setSaving(true);
    try {
      if (isNew) {
        const response = await axios.post(`${API}/admin/courses`, course, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Cours créé!");
        navigate(`/admin/course/${response.data.course_id}`);
      } else {
        await axios.put(`${API}/courses/${courseId}`, course, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Cours enregistré!");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Veuillez sélectionner une image");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image trop volumineuse (max 5 Mo)");
      return;
    }

    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await axios.post(`${API}/upload/image`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });
      // Store an absolute URL so previews and cards work everywhere
      setCourse((prev) => ({ ...prev, thumbnail: `${API}${response.data.url}` }));
      toast.success("Image téléversée!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors du téléversement");
    } finally {
      setUploadingImage(false);
      e.target.value = "";
    }
  };

  const publishCourse = async () => {
    try {
      await axios.patch(`${API}/courses/${courseId}/publish`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCourse({ ...course, is_published: true });
      toast.success("Cours publié!");
    } catch (error) {
      toast.error("Erreur lors de la publication");
    }
  };

  const addTag = () => {
    if (newTag && !course.tags.includes(newTag.toLowerCase())) {
      setCourse({ ...course, tags: [...course.tags, newTag.toLowerCase()] });
      setNewTag("");
    }
  };

  const removeTag = (tag) => {
    setCourse({ ...course, tags: course.tags.filter(t => t !== tag) });
  };

  // Lesson management
  const saveLesson = async () => {
    if (!editingLesson?.title) {
      toast.error("Titre requis");
      return;
    }

    try {
      if (editingLesson.lesson_id) {
        // Update existing
        await axios.put(`${API}/lessons/${editingLesson.lesson_id}`, editingLesson, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Leçon mise à jour!");
      } else {
        // Create new
        await axios.post(`${API}/lessons`, {
          ...editingLesson,
          course_id: courseId,
          order: lessons.length + 1,
        }, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Leçon créée!");
      }
      setShowLessonDialog(false);
      setEditingLesson(null);
      fetchLessons();
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement");
    }
  };

  // ===== Agent IA : générer / appliquer le plan =====
  const generateOutline = async () => {
    if (!course.title) {
      toast.error("Renseignez d'abord le titre du cours");
      return;
    }
    setAiLoading(true);
    setAiOutline(null);
    try {
      const body = {
        title: course.title,
        description: course.description || course.short_description || "",
        level: course.level || "beginner",
        language: "fr",
        num_modules: Number(aiParams.num_modules) || 5,
        lessons_per_module: Number(aiParams.lessons_per_module) || 4,
      };
      if (aiParams.reorganize && lessons.length > 0) {
        body.existing_lessons = lessons.map((l) => l.title);
      }
      const response = await axios.post(`${API}/admin/ai/course-outline`, body, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAiOutline(response.data.modules || []);
      toast.success("Plan généré par l'IA");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Échec de la génération IA");
    } finally {
      setAiLoading(false);
    }
  };

  const applyOutline = async () => {
    if (!aiOutline || aiOutline.length === 0) return;
    setApplyingOutline(true);
    try {
      let order = lessons.length; // par défaut : à la suite des leçons existantes

      // Option : remplacer toutes les leçons existantes
      if (aiParams.replace && lessons.length > 0) {
        for (const l of lessons) {
          await axios.delete(`${API}/lessons/${l.lesson_id}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
        }
        order = 0;
      }

      for (let mIdx = 0; mIdx < aiOutline.length; mIdx++) {
        const mod = aiOutline[mIdx];
        for (const les of mod.lessons || []) {
          order += 1;
          await axios.post(
            `${API}/lessons`,
            {
              course_id: courseId,
              title: les.title,
              description: les.description || "",
              video_url: "",
              duration_minutes: Number(les.duration_minutes) || 10,
              is_free: false,
              order,
              module: mod.title,
              module_order: mIdx,
            },
            { headers: { Authorization: `Bearer ${token}` } }
          );
        }
      }
      toast.success("Plan appliqué : leçons créées !");
      setShowAiDialog(false);
      setAiOutline(null);
      fetchLessons();
    } catch (error) {
      toast.error("Erreur lors de la création des leçons");
    } finally {
      setApplyingOutline(false);
    }
  };

  // ===== Agent IA : script vidéo d'une leçon =====
  const openScriptDialog = (lesson) => {
    setScriptLesson(lesson);
    setScriptDetails(lesson.description || "");
    setScriptHtml(lesson.script_html || "");
    setShowScriptDialog(true);
  };

  const generateScript = async () => {
    if (!scriptLesson) return;
    setScriptLoading(true);
    try {
      const response = await axios.post(
        `${API}/admin/ai/lesson-script`,
        {
          course_title: course.title,
          module: scriptLesson.module || "",
          title: scriptLesson.title,
          description: scriptLesson.description || "",
          level: course.level || "beginner",
          duration_minutes: Number(scriptLesson.duration_minutes) || 10,
          details: scriptDetails,
          language: "fr",
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const html = response.data.html || "";
      setScriptHtml(html);

      // Enregistrement automatique sur la leçon (pour ne pas perdre le script)
      try {
        await axios.put(
          `${API}/lessons/${scriptLesson.lesson_id}`,
          { script_html: html },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        // Mise à jour immédiate de l'état local (badge + export)
        setLessons((prev) =>
          prev.map((l) =>
            l.lesson_id === scriptLesson.lesson_id ? { ...l, script_html: html } : l
          )
        );
        setScriptLesson((prev) => (prev ? { ...prev, script_html: html } : prev));
        toast.success("Script généré et enregistré sur la leçon");
      } catch (e) {
        toast.success("Script généré (pense à l'enregistrer)");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Échec de la génération du script");
    } finally {
      setScriptLoading(false);
    }
  };

  const saveScript = async () => {
    if (!scriptLesson || !scriptHtml) return;
    setSavingScript(true);
    try {
      await axios.put(
        `${API}/lessons/${scriptLesson.lesson_id}`,
        { script_html: scriptHtml },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setLessons((prev) =>
        prev.map((l) =>
          l.lesson_id === scriptLesson.lesson_id ? { ...l, script_html: scriptHtml } : l
        )
      );
      toast.success("Script enregistré sur la leçon");
      setShowScriptDialog(false);
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement du script");
    } finally {
      setSavingScript(false);
    }
  };

  const downloadScript = () => {
    if (!scriptHtml) return;
    const blob = new Blob([scriptHtml], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const safe = (scriptLesson?.title || "script").replace(/[^\w\-]+/g, "_");
    a.href = url;
    a.download = `script_${safe}.html`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ===== Export : tous les scripts du cours en un seul HTML =====
  const exportAllScripts = () => {
    const withScripts = lessons.filter((l) => l.script_html && l.script_html.trim());
    if (withScripts.length === 0) {
      toast.error("Aucun script généré. Génère d'abord des scripts de leçon.");
      return;
    }

    const esc = (s) =>
      (s || "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");

    // Regroupement par module en conservant l'ordre
    const groups = [];
    const byTitle = {};
    lessons.forEach((l) => {
      const title = l.module?.trim() || "Contenu du cours";
      if (!byTitle[title]) {
        byTitle[title] = { title, lessons: [] };
        groups.push(byTitle[title]);
      }
      byTitle[title].lessons.push(l);
    });

    let globalIdx = 0;
    let toc = "";
    let body = "";

    groups.forEach((g, gi) => {
      const groupLessons = g.lessons.filter((l) => l.script_html && l.script_html.trim());
      if (groupLessons.length === 0) return;
      toc += `<li class="toc-module"><span>${gi + 1}. ${esc(g.title)}</span><ul>`;
      body += `<section class="module"><h2 class="module-title">${gi + 1}. ${esc(g.title)}</h2>`;
      groupLessons.forEach((l) => {
        globalIdx += 1;
        const anchor = `lesson-${l.lesson_id}`;
        toc += `<li><a href="#${anchor}">${globalIdx}. ${esc(l.title)}</a></li>`;
        body += `
          <article class="lesson" id="${anchor}">
            <div class="lesson-head">
              <span class="badge">Leçon ${globalIdx}</span>
              <h3>${esc(l.title)}</h3>
              <span class="meta">${Number(l.duration_minutes) || 0} min</span>
            </div>
            <iframe class="script-frame" loading="lazy" srcdoc="${esc(l.script_html)}"
              onload="this.style.height=(this.contentWindow.document.documentElement.scrollHeight+30)+'px'"></iframe>
          </article>`;
      });
      toc += `</ul></li>`;
      body += `</section>`;
    });

    const now = new Date().toLocaleDateString("fr-FR");
    const doc = `<!DOCTYPE html>
<html lang="fr">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>Scripts — ${esc(course.title)}</title>
<style>
  :root { --bg:#0b0f14; --card:#131a22; --border:#233040; --fg:#e6edf3; --muted:#8b98a5; --accent:#22c55e; }
  * { box-sizing:border-box; }
  body { margin:0; background:var(--bg); color:var(--fg); font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif; line-height:1.6; }
  .wrap { max-width:1000px; margin:0 auto; padding:32px 20px 80px; }
  .cover { text-align:center; padding:48px 0 24px; border-bottom:1px solid var(--border); margin-bottom:32px; }
  .cover h1 { font-size:2.2rem; margin:0 0 8px; }
  .cover p { color:var(--muted); margin:4px 0; }
  .toc { background:var(--card); border:1px solid var(--border); border-radius:12px; padding:20px 24px; margin-bottom:40px; }
  .toc h2 { margin:0 0 12px; font-size:1.1rem; }
  .toc ul { list-style:none; padding-left:0; margin:0; }
  .toc .toc-module > span { display:block; font-weight:700; margin:12px 0 4px; }
  .toc .toc-module ul { padding-left:16px; }
  .toc a { color:var(--fg); text-decoration:none; display:inline-block; padding:3px 0; }
  .toc a:hover { color:var(--accent); }
  .module-title { font-size:1.5rem; border-left:4px solid var(--accent); padding-left:12px; margin:40px 0 16px; }
  .lesson { background:var(--card); border:1px solid var(--border); border-radius:12px; margin:0 0 24px; overflow:hidden; }
  .lesson-head { display:flex; align-items:center; gap:12px; padding:14px 18px; border-bottom:1px solid var(--border); background:rgba(255,255,255,.02); }
  .lesson-head h3 { margin:0; font-size:1.1rem; flex:1; }
  .badge { background:var(--accent); color:#04120a; font-weight:700; font-size:.72rem; padding:3px 8px; border-radius:999px; }
  .meta { color:var(--muted); font-size:.85rem; }
  .script-frame { width:100%; border:0; background:#fff; display:block; }
  @media print { .toc a { color:#000; } body { background:#fff; color:#000; } }
</style>
</head>
<body>
  <div class="wrap">
    <div class="cover">
      <h1>${esc(course.title)}</h1>
      <p>Scripts vidéo — ${withScripts.length} leçon(s)</p>
      <p>Niveau : ${esc(course.level || "")} • Exporté le ${now}</p>
    </div>
    <nav class="toc">
      <h2>Sommaire</h2>
      <ul>${toc}</ul>
    </nav>
    ${body}
  </div>
</body>
</html>`;

    const blob = new Blob([doc], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const safe = (course.title || "cours").replace(/[^\w\-]+/g, "_");
    a.href = url;
    a.download = `scripts_${safe}.html`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Export : ${withScripts.length} script(s) dans un seul HTML`);
  };

  const deleteLesson = async (lessonId) => {
    try {
      await axios.delete(`${API}/lessons/${lessonId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Leçon supprimée!");
      fetchLessons();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  // Lab management
  const saveLab = async () => {
    if (!editingLab?.title || !selectedLessonId) {
      toast.error("Titre et leçon requis");
      return;
    }

    try {
      if (editingLab.lab_id) {
        await axios.put(`${API}/labs/${editingLab.lab_id}`, editingLab, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Lab mis à jour!");
      } else {
        await axios.post(`${API}/labs`, {
          ...editingLab,
          lesson_id: selectedLessonId,
          course_id: courseId,
        }, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Lab créé!");
      }
      setShowLabDialog(false);
      setEditingLab(null);
      setSelectedLessonId(null);
      fetchLessons();
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement");
    }
  };

  const deleteLab = async (labId) => {
    try {
      await axios.delete(`${API}/labs/${labId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Lab supprimé!");
      fetchLessons();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  // Quiz management
  const saveQuiz = async () => {
    if (!editingQuiz?.title || !selectedLessonId) {
      toast.error("Titre et leçon requis");
      return;
    }

    try {
      if (editingQuiz.quiz_id) {
        await axios.put(`${API}/quizzes/${editingQuiz.quiz_id}`, editingQuiz, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Quiz mis à jour!");
      } else {
        await axios.post(`${API}/quizzes`, {
          ...editingQuiz,
          lesson_id: selectedLessonId,
          course_id: courseId,
        }, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Quiz créé!");
      }
      setShowQuizDialog(false);
      setEditingQuiz(null);
      setSelectedLessonId(null);
      fetchLessons();
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement");
    }
  };

  const deleteQuiz = async (quizId) => {
    try {
      await axios.delete(`${API}/quizzes/${quizId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Quiz supprimé!");
      fetchLessons();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  // Quiz question management
  const addQuestion = () => {
    setEditingQuiz({
      ...editingQuiz,
      questions: [
        ...(editingQuiz.questions || []),
        {
          question: "",
          options: ["", "", "", ""],
          correct_answer: 0,
          explanation: "",
        },
      ],
    });
  };

  const generateQuizQuestions = async () => {
    const topic = editingQuiz?.title?.trim();
    if (!topic) {
      toast.error("Renseignez d'abord le titre du quiz (sujet)");
      return;
    }
    const lesson = lessons.find((l) => l.lesson_id === selectedLessonId);
    setQuizAiLoading(true);
    try {
      const response = await axios.post(
        `${API}/admin/ai/quiz`,
        {
          topic,
          course_title: course.title,
          lesson_title: lesson?.title || "",
          num_questions: quizNumQuestions,
          difficulty: course.level,
          language: "fr",
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const generated = response.data.questions || [];
      setEditingQuiz((prev) => ({
        ...prev,
        questions: [...(prev?.questions || []), ...generated],
      }));
      toast.success(`${generated.length} question(s) générée(s) par l'IA`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Échec de la génération IA");
    } finally {
      setQuizAiLoading(false);
    }
  };

  const updateQuestion = (index, field, value) => {
    const newQuestions = [...editingQuiz.questions];
    newQuestions[index] = { ...newQuestions[index], [field]: value };
    setEditingQuiz({ ...editingQuiz, questions: newQuestions });
  };

  const updateQuestionOption = (qIndex, oIndex, value) => {
    const newQuestions = [...editingQuiz.questions];
    newQuestions[qIndex].options[oIndex] = value;
    setEditingQuiz({ ...editingQuiz, questions: newQuestions });
  };

  const removeQuestion = (index) => {
    const newQuestions = editingQuiz.questions.filter((_, i) => i !== index);
    setEditingQuiz({ ...editingQuiz, questions: newQuestions });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="course-editor">
      <Navbar />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Link to="/admin">
              <Button variant="ghost" size="icon">
                <ChevronLeft className="w-5 h-5" />
              </Button>
            </Link>
            <div>
              <h1 className="text-2xl font-heading font-bold">
                {isNew ? "Nouveau cours" : "Modifier le cours"}
              </h1>
              {!isNew && (
                <div className="flex items-center gap-2 mt-1">
                  {course.is_published ? (
                    <Badge className="bg-primary/20 text-primary">Publié</Badge>
                  ) : (
                    <Badge variant="outline">Brouillon</Badge>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            {!isNew && !course.is_published && (
              <Button variant="outline" onClick={publishCourse}>
                <Eye className="w-4 h-4 mr-2" />
                Publier
              </Button>
            )}
            <Button onClick={saveCourse} disabled={saving} className="glow-green">
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Enregistrer
            </Button>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="details">Détails du cours</TabsTrigger>
            {!isNew && <TabsTrigger value="lessons">Leçons & Contenu</TabsTrigger>}
          </TabsList>

          {/* Details Tab */}
          <TabsContent value="details" className="space-y-6 mt-6">
            <div className="grid lg:grid-cols-3 gap-6">
              {/* Main Info */}
              <div className="lg:col-span-2 space-y-6">
                <div className="bg-card border border-border rounded-lg p-6 space-y-4">
                  <h2 className="font-heading font-bold text-lg">Informations générales</h2>
                  
                  <div>
                    <Label>Titre du cours *</Label>
                    <Input
                      value={course.title}
                      onChange={(e) => setCourse({ ...course, title: e.target.value })}
                      placeholder="Docker pour les débutants"
                      className="mt-1"
                      data-testid="course-title-input"
                    />
                  </div>

                  <div>
                    <Label>Description courte *</Label>
                    <Input
                      value={course.short_description}
                      onChange={(e) => setCourse({ ...course, short_description: e.target.value })}
                      placeholder="Maîtrisez Docker en quelques heures"
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label>Description complète *</Label>
                    <Textarea
                      value={course.description}
                      onChange={(e) => setCourse({ ...course, description: e.target.value })}
                      placeholder="Description détaillée du cours..."
                      rows={5}
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label>Image de couverture</Label>
                    <div className="mt-1 flex items-center gap-3">
                      <input
                        id="cover-image-input"
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        disabled={uploadingImage}
                        className="hidden"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        disabled={uploadingImage}
                        onClick={() => document.getElementById("cover-image-input").click()}
                      >
                        {uploadingImage ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Téléversement...
                          </>
                        ) : (
                          <>
                            <Upload className="w-4 h-4 mr-2" />
                            {course.thumbnail ? "Changer l'image" : "Choisir une image"}
                          </>
                        )}
                      </Button>
                      {course.thumbnail && (
                        <button
                          type="button"
                          onClick={() => setCourse({ ...course, thumbnail: "" })}
                          className="text-sm text-muted-foreground hover:text-destructive"
                        >
                          Retirer
                        </button>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      JPG, PNG, WEBP, GIF ou SVG — 5 Mo max.
                    </p>
                    {course.thumbnail && (
                      <img
                        src={course.thumbnail}
                        alt="Thumbnail preview"
                        className="mt-2 w-full max-w-md rounded-lg"
                      />
                    )}
                  </div>
                </div>

                {/* Tags */}
                <div className="bg-card border border-border rounded-lg p-6">
                  <h2 className="font-heading font-bold text-lg mb-4">Tags</h2>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {course.tags?.map((tag) => (
                      <Badge key={tag} variant="outline" className="flex items-center gap-1">
                        {tag}
                        <button onClick={() => removeTag(tag)} className="ml-1">
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <Input
                      value={newTag}
                      onChange={(e) => setNewTag(e.target.value)}
                      placeholder="Ajouter un tag..."
                      onKeyPress={(e) => e.key === "Enter" && addTag()}
                    />
                    <Button variant="outline" onClick={addTag}>
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>

              {/* Sidebar */}
              <div className="space-y-6">
                <div className="bg-card border border-border rounded-lg p-6 space-y-4">
                  <h2 className="font-heading font-bold text-lg">Configuration</h2>

                  <div>
                    <Label>Catégorie</Label>
                    <Select
                      value={course.category}
                      onValueChange={(v) => setCourse({ ...course, category: v })}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="docker">Docker</SelectItem>
                        <SelectItem value="kubernetes">Kubernetes</SelectItem>
                        <SelectItem value="aws">AWS</SelectItem>
                        <SelectItem value="azure">Azure</SelectItem>
                        <SelectItem value="gcp">GCP</SelectItem>
                        <SelectItem value="devops">DevOps / CI-CD</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Niveau</Label>
                    <Select
                      value={course.level}
                      onValueChange={(v) => setCourse({ ...course, level: v })}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="beginner">Débutant</SelectItem>
                        <SelectItem value="intermediate">Intermédiaire</SelectItem>
                        <SelectItem value="advanced">Avancé</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Prix ($)</Label>
                    <Input
                      type="number"
                      value={course.price}
                      onChange={(e) => setCourse({ ...course, price: parseFloat(e.target.value) || 0 })}
                      min={0}
                      step={0.01}
                      className="mt-1"
                    />
                  </div>
                </div>

                {!isNew && (
                  <div className="bg-card border border-border rounded-lg p-6">
                    <h2 className="font-heading font-bold text-lg mb-4">Statistiques</h2>
                    <div className="space-y-3 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Étudiants</span>
                        <span className="font-bold">{course.students_count || 0}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Note</span>
                        <span className="font-bold">{course.rating?.toFixed(1) || "N/A"}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Avis</span>
                        <span className="font-bold">{course.reviews_count || 0}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Leçons</span>
                        <span className="font-bold">{lessons.length}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Lessons Tab */}
          {!isNew && (
            <TabsContent value="lessons" className="mt-6">
              <div className="bg-card border border-border rounded-lg">
                <div className="p-4 border-b border-border flex items-center justify-between gap-2 flex-wrap">
                  <h2 className="font-heading font-bold text-lg">Leçons du cours</h2>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Button
                      variant="outline"
                      className="border-primary/50 text-primary hover:bg-primary/10"
                      onClick={() => {
                        setAiOutline(null);
                        setShowAiDialog(true);
                      }}
                      data-testid="ai-outline-btn"
                    >
                      <Sparkles className="w-4 h-4 mr-2" />
                      Générer le plan avec l'IA
                    </Button>
                    <Button
                      variant="outline"
                      onClick={exportAllScripts}
                      data-testid="export-scripts-btn"
                    >
                      <Upload className="w-4 h-4 mr-2 rotate-180" />
                      Exporter les scripts (HTML)
                    </Button>
                    <Button
                      onClick={() => {
                        setEditingLesson({ title: "", description: "", video_url: "", duration_minutes: 0, is_free: false, module: "" });
                        setShowLessonDialog(true);
                      }}
                      data-testid="add-lesson-btn"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Ajouter une leçon
                    </Button>
                  </div>
                </div>

                {loadingLessons ? (
                  <div className="p-12 text-center">
                    <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto" />
                  </div>
                ) : lessons.length === 0 ? (
                  <div className="p-12 text-center">
                    <Video className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">Aucune leçon pour le moment</p>
                    <Button
                      variant="outline"
                      className="mt-4"
                      onClick={() => {
                        setEditingLesson({ title: "", description: "", video_url: "", duration_minutes: 0, is_free: false });
                        setShowLessonDialog(true);
                      }}
                    >
                      Créer la première leçon
                    </Button>
                  </div>
                ) : (
                  <Accordion type="multiple" className="divide-y divide-border">
                    {lessons.map((lesson, index) => {
                      const prevModule = index > 0 ? (lessons[index - 1].module || "") : null;
                      const curModule = lesson.module || "";
                      const showModuleHeader = curModule && curModule !== prevModule;
                      return (
                      <div key={lesson.lesson_id}>
                      {showModuleHeader && (
                        <div className="px-4 py-2 bg-muted/40 text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-2">
                          <BookOpen className="w-3.5 h-3.5" />
                          {curModule}
                        </div>
                      )}
                      <AccordionItem value={lesson.lesson_id} className="border-0">
                        <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-muted/30">
                          <div className="flex items-center gap-4 flex-1">
                            <div className="w-8 h-8 bg-muted rounded flex items-center justify-center font-mono text-sm">
                              {String(index + 1).padStart(2, "0")}
                            </div>
                            <div className="text-left flex-1">
                              <p className="font-medium">{lesson.title}</p>
                              <div className="flex items-center gap-3 text-sm text-muted-foreground mt-1">
                                <span className="flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  {lesson.duration_minutes} min
                                </span>
                                {lesson.is_free && (
                                  <Badge variant="outline" className="text-xs">Gratuit</Badge>
                                )}
                                {lesson.lab && (
                                  <Badge className="bg-secondary/20 text-secondary text-xs">Lab</Badge>
                                )}
                                {lesson.quiz && (
                                  <Badge className="bg-accent/20 text-accent text-xs">Quiz</Badge>
                                )}
                                {lesson.script_html && (
                                  <Badge className="bg-primary/20 text-primary text-xs">Script</Badge>
                                )}
                              </div>
                            </div>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="px-4 pb-4">
                          <div className="pl-12 space-y-4">
                            <p className="text-sm text-muted-foreground">{lesson.description}</p>
                            
                            {lesson.video_url && (
                              <div className="flex items-center gap-2 text-sm">
                                <Video className="w-4 h-4 text-primary" />
                                <a href={lesson.video_url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline truncate">
                                  {lesson.video_url}
                                </a>
                              </div>
                            )}

                            <div className="flex flex-wrap gap-2 pt-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setEditingLesson(lesson);
                                  setShowLessonDialog(true);
                                }}
                              >
                                <Edit className="w-3 h-3 mr-1" />
                                Modifier
                              </Button>

                              <Button
                                variant="outline"
                                size="sm"
                                className="border-primary/50 text-primary hover:bg-primary/10"
                                onClick={() => openScriptDialog(lesson)}
                              >
                                <Sparkles className="w-3 h-3 mr-1" />
                                {lesson.script_html ? "Script IA" : "Générer le script IA"}
                              </Button>

                              {!lesson.lab ? (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    setSelectedLessonId(lesson.lesson_id);
                                    setEditingLab({ title: "", description: "", instructions: "", environment: "docker", difficulty: "easy", estimated_time_minutes: 30 });
                                    setShowLabDialog(true);
                                  }}
                                >
                                  <Terminal className="w-3 h-3 mr-1" />
                                  Ajouter Lab
                                </Button>
                              ) : (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    setSelectedLessonId(lesson.lesson_id);
                                    setEditingLab(lesson.lab);
                                    setShowLabDialog(true);
                                  }}
                                >
                                  <Terminal className="w-3 h-3 mr-1" />
                                  Modifier Lab
                                </Button>
                              )}

                              {!lesson.quiz ? (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    setSelectedLessonId(lesson.lesson_id);
                                    setEditingQuiz({ title: "", questions: [], passing_score: 70 });
                                    setShowQuizDialog(true);
                                  }}
                                >
                                  <BookOpen className="w-3 h-3 mr-1" />
                                  Ajouter Quiz
                                </Button>
                              ) : (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    setSelectedLessonId(lesson.lesson_id);
                                    setEditingQuiz(lesson.quiz);
                                    setShowQuizDialog(true);
                                  }}
                                >
                                  <BookOpen className="w-3 h-3 mr-1" />
                                  Modifier Quiz
                                </Button>
                              )}

                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button variant="outline" size="sm" className="text-destructive">
                                    <Trash2 className="w-3 h-3 mr-1" />
                                    Supprimer
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Supprimer la leçon?</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      Cette action supprimera la leçon et tout son contenu (lab, quiz).
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Annuler</AlertDialogCancel>
                                    <AlertDialogAction
                                      onClick={() => deleteLesson(lesson.lesson_id)}
                                      className="bg-destructive"
                                    >
                                      Supprimer
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </div>
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                      </div>
                      );
                    })}
                  </Accordion>
                )}
              </div>
            </TabsContent>
          )}
        </Tabs>
      </div>

      {/* Lesson Dialog */}
      <Dialog open={showLessonDialog} onOpenChange={setShowLessonDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editingLesson?.lesson_id ? "Modifier la leçon" : "Nouvelle leçon"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Titre *</Label>
              <Input
                value={editingLesson?.title || ""}
                onChange={(e) => setEditingLesson({ ...editingLesson, title: e.target.value })}
                placeholder="Introduction à Docker"
              />
            </div>
            <div>
              <Label>Module / Section</Label>
              <Input
                value={editingLesson?.module || ""}
                onChange={(e) => setEditingLesson({ ...editingLesson, module: e.target.value })}
                placeholder="Ex: Introduction, Kubernetes Overview..."
                list="module-suggestions"
              />
              <datalist id="module-suggestions">
                {[...new Set(lessons.map((l) => l.module).filter(Boolean))].map((m) => (
                  <option key={m} value={m} />
                ))}
              </datalist>
              <p className="text-xs text-muted-foreground mt-1">
                Les leçons d'un même module sont regroupées dans le sommaire.
              </p>
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={editingLesson?.description || ""}
                onChange={(e) => setEditingLesson({ ...editingLesson, description: e.target.value })}
                placeholder="Description de la leçon..."
                rows={3}
              />
            </div>
            <div>
              <Label>URL de la vidéo (YouTube, Vimeo, etc.)</Label>
              <Input
                value={editingLesson?.video_url || ""}
                onChange={(e) => setEditingLesson({ ...editingLesson, video_url: e.target.value })}
                placeholder="https://www.youtube.com/watch?v=..."
              />
              <p className="text-xs text-muted-foreground mt-1">
                Formats supportés : YouTube, Vimeo, Google Drive (lien de partage) ou lien direct MP4
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Durée (minutes)</Label>
                <Input
                  type="number"
                  value={editingLesson?.duration_minutes || 0}
                  onChange={(e) => setEditingLesson({ ...editingLesson, duration_minutes: parseInt(e.target.value) || 0 })}
                  min={0}
                />
              </div>
              <div className="flex items-center gap-3 pt-6">
                <Switch
                  checked={editingLesson?.is_free || false}
                  onCheckedChange={(checked) => setEditingLesson({ ...editingLesson, is_free: checked })}
                />
                <Label>Leçon gratuite (aperçu)</Label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLessonDialog(false)}>
              Annuler
            </Button>
            <Button onClick={saveLesson}>
              {editingLesson?.lesson_id ? "Mettre à jour" : "Créer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI Outline Dialog - Agent IA */}
      <Dialog open={showAiDialog} onOpenChange={setShowAiDialog}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-primary" />
              Agent IA — Plan de cours
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="bg-muted/40 rounded-lg p-3 text-sm">
              <p className="font-medium">{course.title || "Sans titre"}</p>
              <p className="text-muted-foreground text-xs mt-1">
                Niveau : {course.level} • L'IA génère des modules et leçons à partir du titre et de la description du cours.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Nombre de modules</Label>
                <Input
                  type="number"
                  min={1}
                  max={20}
                  value={aiParams.num_modules}
                  onChange={(e) => setAiParams({ ...aiParams, num_modules: e.target.value })}
                />
              </div>
              <div>
                <Label>Leçons par module</Label>
                <Input
                  type="number"
                  min={1}
                  max={20}
                  value={aiParams.lessons_per_module}
                  onChange={(e) => setAiParams({ ...aiParams, lessons_per_module: e.target.value })}
                />
              </div>
            </div>

            {lessons.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Switch
                    checked={aiParams.reorganize}
                    onCheckedChange={(checked) => setAiParams({ ...aiParams, reorganize: checked })}
                  />
                  <Label className="text-sm">
                    Réorganiser les {lessons.length} leçons existantes en modules
                  </Label>
                </div>
                <div className="flex items-center gap-3">
                  <Switch
                    checked={aiParams.replace}
                    onCheckedChange={(checked) => setAiParams({ ...aiParams, replace: checked })}
                  />
                  <Label className="text-sm">
                    Remplacer les leçons existantes
                    <span className="block text-xs text-destructive">
                      ⚠️ Supprime les {lessons.length} leçons actuelles avant d'appliquer le plan
                    </span>
                  </Label>
                </div>
              </div>
            )}

            <Button onClick={generateOutline} disabled={aiLoading} className="w-full">
              {aiLoading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Génération en cours...</>
              ) : (
                <><Wand2 className="w-4 h-4 mr-2" /> Générer le plan</>
              )}
            </Button>

            {/* Aperçu du plan généré */}
            {aiOutline && (
              <div className="border border-border rounded-lg divide-y divide-border">
                <div className="p-3 bg-muted/30 text-sm font-medium">
                  {aiOutline.length} modules •{" "}
                  {aiOutline.reduce((n, m) => n + (m.lessons?.length || 0), 0)} leçons proposées
                </div>
                <div className="max-h-72 overflow-y-auto">
                  {aiOutline.map((mod, i) => (
                    <div key={i} className="p-3">
                      <p className="font-semibold text-sm">
                        {i + 1}. {mod.title}
                      </p>
                      {mod.description && (
                        <p className="text-xs text-muted-foreground mb-2">{mod.description}</p>
                      )}
                      <ul className="space-y-1 pl-4">
                        {mod.lessons?.map((les, j) => (
                          <li key={j} className="text-sm flex items-center gap-2">
                            <Play className="w-3 h-3 text-muted-foreground shrink-0" />
                            <span className="flex-1">{les.title}</span>
                            <span className="text-xs text-muted-foreground flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {les.duration_minutes} min
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAiDialog(false)}>
              Fermer
            </Button>
            {aiOutline && (
              <Button onClick={applyOutline} disabled={applyingOutline} className="glow-green">
                {applyingOutline ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Application...</>
                ) : (
                  <>{aiParams.replace ? "Remplacer par" : "Appliquer"} ({aiOutline.reduce((n, m) => n + (m.lessons?.length || 0), 0)} leçons)</>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Script IA Dialog */}
      <Dialog open={showScriptDialog} onOpenChange={setShowScriptDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-primary" />
              Script vidéo IA — {scriptLesson?.title}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div>
              <Label>Détails à couvrir dans la vidéo</Label>
              <Textarea
                value={scriptDetails}
                onChange={(e) => setScriptDetails(e.target.value)}
                placeholder="Liste les points, commandes, démos, exemples que tu veux enregistrer dans cette vidéo. L'IA en fera un script complet."
                rows={5}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Plus tu donnes de détails, plus le script sera précis (voix off + actions à l'écran + code).
              </p>
            </div>

            <div className="flex flex-wrap gap-2">
              <Button onClick={generateScript} disabled={scriptLoading}>
                {scriptLoading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Génération...</>
                ) : (
                  <><Wand2 className="w-4 h-4 mr-2" /> {scriptHtml ? "Régénérer" : "Générer le script"}</>
                )}
              </Button>
              {scriptHtml && (
                <Button variant="outline" onClick={downloadScript}>
                  <Upload className="w-4 h-4 mr-2 rotate-180" />
                  Télécharger HTML
                </Button>
              )}
              {scriptHtml && (
                <Button
                  variant="outline"
                  onClick={() => {
                    const w = window.open("", "_blank");
                    if (w) { w.document.write(scriptHtml); w.document.close(); }
                  }}
                >
                  <Eye className="w-4 h-4 mr-2" />
                  Ouvrir en plein écran
                </Button>
              )}
            </div>

            {/* Aperçu rendu du script */}
            {scriptHtml ? (
              <div className="border border-border rounded-lg overflow-hidden">
                <div className="px-3 py-2 bg-muted/30 text-xs text-muted-foreground">
                  Aperçu du script
                </div>
                <iframe
                  title="script-preview"
                  srcDoc={scriptHtml}
                  className="w-full bg-white"
                  style={{ height: "50vh", border: "0" }}
                />
              </div>
            ) : (
              <div className="border border-dashed border-border rounded-lg p-8 text-center text-sm text-muted-foreground">
                Renseigne les détails puis clique sur « Générer le script ».
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowScriptDialog(false)}>
              Fermer
            </Button>
            {scriptHtml && (
              <Button onClick={saveScript} disabled={savingScript} className="glow-green">
                {savingScript ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Enregistrement...</>
                ) : (
                  <><Save className="w-4 h-4 mr-2" /> Enregistrer sur la leçon</>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Lab Dialog */}
      <Dialog open={showLabDialog} onOpenChange={setShowLabDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingLab?.lab_id ? "Modifier le lab" : "Nouveau lab"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Titre *</Label>
              <Input
                value={editingLab?.title || ""}
                onChange={(e) => setEditingLab({ ...editingLab, title: e.target.value })}
                placeholder="Créer votre premier conteneur"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={editingLab?.description || ""}
                onChange={(e) => setEditingLab({ ...editingLab, description: e.target.value })}
                placeholder="Description du lab..."
                rows={2}
              />
            </div>
            <div>
              <Label>Instructions (Markdown supporté)</Label>
              <Textarea
                value={editingLab?.instructions || ""}
                onChange={(e) => setEditingLab({ ...editingLab, instructions: e.target.value })}
                placeholder="1. Exécutez la commande...&#10;2. Vérifiez que..."
                rows={6}
                className="font-mono text-sm"
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Environnement</Label>
                <Select
                  value={editingLab?.environment || "docker"}
                  onValueChange={(v) => setEditingLab({ ...editingLab, environment: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="docker">Docker</SelectItem>
                    <SelectItem value="kubernetes">Kubernetes</SelectItem>
                    <SelectItem value="terraform">Terraform</SelectItem>
                    <SelectItem value="ansible">Ansible</SelectItem>
                    <SelectItem value="linux">Linux</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Difficulté</Label>
                <Select
                  value={editingLab?.difficulty || "easy"}
                  onValueChange={(v) => setEditingLab({ ...editingLab, difficulty: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="easy">Facile</SelectItem>
                    <SelectItem value="medium">Moyen</SelectItem>
                    <SelectItem value="hard">Difficile</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Temps estimé (min)</Label>
                <Input
                  type="number"
                  value={editingLab?.estimated_time_minutes || 30}
                  onChange={(e) => setEditingLab({ ...editingLab, estimated_time_minutes: parseInt(e.target.value) || 30 })}
                  min={5}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            {editingLab?.lab_id && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" className="mr-auto text-destructive">
                    <Trash2 className="w-4 h-4 mr-2" />
                    Supprimer
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Supprimer le lab?</AlertDialogTitle>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Annuler</AlertDialogCancel>
                    <AlertDialogAction onClick={() => { deleteLab(editingLab.lab_id); setShowLabDialog(false); }} className="bg-destructive">
                      Supprimer
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
            <Button variant="outline" onClick={() => setShowLabDialog(false)}>
              Annuler
            </Button>
            <Button onClick={saveLab}>
              {editingLab?.lab_id ? "Mettre à jour" : "Créer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quiz Dialog */}
      <Dialog open={showQuizDialog} onOpenChange={setShowQuizDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingQuiz?.quiz_id ? "Modifier le quiz" : "Nouveau quiz"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Titre *</Label>
                <Input
                  value={editingQuiz?.title || ""}
                  onChange={(e) => setEditingQuiz({ ...editingQuiz, title: e.target.value })}
                  placeholder="Quiz: Les bases de Docker"
                />
              </div>
              <div>
                <Label>Score minimum (%)</Label>
                <Input
                  type="number"
                  value={editingQuiz?.passing_score || 70}
                  onChange={(e) => setEditingQuiz({ ...editingQuiz, passing_score: parseInt(e.target.value) || 70 })}
                  min={0}
                  max={100}
                />
              </div>
            </div>

            {/* Génération par IA */}
            <div className="border border-primary/30 bg-primary/5 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-4 h-4 text-primary" />
                <Label className="font-bold">Générer avec l'IA</Label>
              </div>
              <p className="text-sm text-muted-foreground mb-3">
                L'IA rédige des questions QCM à partir du <strong>titre</strong> du quiz et du contexte du cours.
              </p>
              <div className="flex items-end gap-3">
                <div className="w-32">
                  <Label className="text-xs">Nb. de questions</Label>
                  <Input
                    type="number"
                    min={1}
                    max={20}
                    value={quizNumQuestions}
                    onChange={(e) => setQuizNumQuestions(parseInt(e.target.value) || 5)}
                  />
                </div>
                <Button onClick={generateQuizQuestions} disabled={quizAiLoading}>
                  {quizAiLoading ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Génération...</>
                  ) : (
                    <><Wand2 className="w-4 h-4 mr-2" /> Générer les questions</>
                  )}
                </Button>
              </div>
            </div>

            <div className="border-t border-border pt-4">
              <div className="flex items-center justify-between mb-4">
                <Label className="text-base font-bold">Questions</Label>
                <Button variant="outline" size="sm" onClick={addQuestion}>
                  <Plus className="w-4 h-4 mr-1" />
                  Ajouter une question
                </Button>
              </div>

              <div className="space-y-6">
                {editingQuiz?.questions?.map((q, qIndex) => (
                  <div key={qIndex} className="bg-muted/30 rounded-lg p-4 space-y-3">
                    <div className="flex items-start justify-between">
                      <Label>Question {qIndex + 1}</Label>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeQuestion(qIndex)}
                        className="text-destructive"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                    <Input
                      value={q.question}
                      onChange={(e) => updateQuestion(qIndex, "question", e.target.value)}
                      placeholder="Qu'est-ce que Docker?"
                    />
                    <div className="space-y-2">
                      <Label className="text-sm">Options (cliquez sur la bonne réponse)</Label>
                      {q.options.map((opt, oIndex) => (
                        <div key={oIndex} className="flex items-center gap-2">
                          <button
                            onClick={() => updateQuestion(qIndex, "correct_answer", oIndex)}
                            className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${
                              q.correct_answer === oIndex
                                ? "border-primary bg-primary text-primary-foreground"
                                : "border-muted-foreground"
                            }`}
                          >
                            {q.correct_answer === oIndex && <CheckCircle className="w-4 h-4" />}
                          </button>
                          <Input
                            value={opt}
                            onChange={(e) => updateQuestionOption(qIndex, oIndex, e.target.value)}
                            placeholder={`Option ${oIndex + 1}`}
                            className="flex-1"
                          />
                        </div>
                      ))}
                    </div>
                    <div>
                      <Label className="text-sm">Explication (optionnel)</Label>
                      <Input
                        value={q.explanation || ""}
                        onChange={(e) => updateQuestion(qIndex, "explanation", e.target.value)}
                        placeholder="Explication de la bonne réponse..."
                      />
                    </div>
                  </div>
                ))}
              </div>

              {(!editingQuiz?.questions || editingQuiz.questions.length === 0) && (
                <div className="text-center py-8 text-muted-foreground">
                  <BookOpen className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>Aucune question. Cliquez sur "Ajouter une question" pour commencer.</p>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            {editingQuiz?.quiz_id && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" className="mr-auto text-destructive">
                    <Trash2 className="w-4 h-4 mr-2" />
                    Supprimer
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Supprimer le quiz?</AlertDialogTitle>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Annuler</AlertDialogCancel>
                    <AlertDialogAction onClick={() => { deleteQuiz(editingQuiz.quiz_id); setShowQuizDialog(false); }} className="bg-destructive">
                      Supprimer
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
            <Button variant="outline" onClick={() => setShowQuizDialog(false)}>
              Annuler
            </Button>
            <Button onClick={saveQuiz}>
              {editingQuiz?.quiz_id ? "Mettre à jour" : "Créer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CourseEditor;
